import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import trainingbg from "../assets/training.jpg";
import trainingpic from "../assets/training/training.jpg";
import PersonIcon from "@mui/icons-material/Person";
import LineBreak from "./LineBreak";
import CircleIcon from "@mui/icons-material/Circle";
import HotelClassIcon from "@mui/icons-material/HotelClass";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import EmailIcon from "@mui/icons-material/Email";

export default function TrainingDetailsSection(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const whoItems =
    (t("trainingDetails.whoItems", {
      returnObjects: true,
    }) as string[]) || [];

  const charItems =
    (t("trainingDetails.charItems", {
      returnObjects: true,
    }) as string[]) || [];

  const formatItems =
    (t("trainingDetails.formatItems", {
      returnObjects: true,
    }) as string[]) || [];

  const exampleTrainings =
    (t("trainingDetails.exampleItems", {
      returnObjects: true,
    }) as {
      title: string;
      listItems: string[];
      description?: string;
    }[]) || [];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "visible", // allow text to exceed image height
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          height: { xs: 300, md: 500 },
          backgroundImage: `url(${trainingbg})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 75%",
          opacity: 0.25,
        }}
      />

      {/* Overlapping Text */}
      <Box
        id="trainingDetails"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          mt: { xs: "-200px", md: "-400px" }, // pulls text upward over image
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={3}
          sx={{
            alignItems: "center",
            color: "white",
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              mt: 3,
              mx: "auto",
              width: { xs: "95%", md: "75%" },
              textAlign: "center",
            }}
          >
            <Typography variant="h4" color="text.primary" gutterBottom>
              {t("trainingDetails.title")}
            </Typography>
          </Box>
          <Box sx={{ mt: 2, mx: "auto", width: { xs: "95%", md: "75%" } }}>
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <PersonIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingDetails.whoTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingDetails.whoStart")}
            </Typography>
            <List dense>
              {whoItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingDetails.whoEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <HotelClassIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingDetails.charTitle")}
            </Typography>
            <List dense>
              {charItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <DashboardIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingDetails.formatTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("trainingDetails.formatStart")}
            </Typography>
            <List dense>
              {formatItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="trainingDetails.formatEnd"
                components={{
                  1: (
                    <a href="/training/rates" className="training-rates-link" />
                  ),
                }}
              />
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <LibraryBooksIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("trainingDetails.exampleTitle")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 4,
              }}
            >
              <Box
                component="img"
                src={trainingpic}
                sx={{
                  width: { xs: "100%", md: "75%" }, // full row on mobile, centered on desktop
                  objectFit: "cover",
                  borderRadius: 3,
                }}
              />
            </Box>
            {exampleTrainings.map((training) => (
              <Box key={training.title} sx={{ textAlign: "left", mt: 2 }}>
                <Typography variant="h6" color="text.primary" gutterBottom>
                  {training.title}
                </Typography>
                <List dense>
                  {training.listItems.map((item) => (
                    <ListItem key={item}>
                      <ListItemIcon>
                        <CircleIcon sx={{ mr: 1 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        sx={{ color: "text.primary" }}
                      />
                    </ListItem>
                  ))}
                </List>
                {training.description ? (
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{ mt: 1 }}
                  >
                    {training.description}
                  </Typography>
                ) : null}
              </Box>
            ))}
            {i18n.resolvedLanguage === "ja" && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="h5"
                  color="text.primary"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  <QuestionAnswerIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                  {t("trainingDetails.inquiryTitle")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.primary"
                  sx={{ mt: 1, lineHeight: 1.2 }}
                >
                  <Trans
                    i18nKey="trainingDetails.inquiryContent"
                    components={{ br: <LineBreak /> }}
                  />
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    mx: "auto",
                    width: { xs: "95%", md: "75%" },
                    textAlign: "center",
                  }}
                >
                  <Button
                    component="a"
                    href="mailto:sharkshooter2011@gmail.com"
                    endIcon={<EmailIcon />}
                    size="large"
                    variant="contained"
                    sx={{
                      px: 5,
                      py: 1.8,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: 999,
                      textTransform: "none",
                      boxShadow: 3,
                      transition: "all 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    {t("research.contactBtnTitle")}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
